import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Chip,
  Typography,
  Stack,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Add, Clear } from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { 
  addSerialNumber, 
  removeSerialNumber, 
  clearAllSerialNumbers,
  setSerialNumbers
} from '../../store/slices/adminRouteSlice';
import type { RootState } from '../../store';

interface SerialNumberChipsProps {
  disabled?: boolean;
}

const SerialNumberChips: React.FC<SerialNumberChipsProps> = ({ disabled = false }) => {
  const [inputValue, setInputValue] = useState('');
  const dispatch = useAppDispatch();
  const { serialNumbers } = useAppSelector((state: RootState) => state.adminRoutes);

  const handleAddSerial = () => {
    const value = inputValue.trim();
    if (value) {
      // Check if input contains comma-separated values
      if (value.includes(',')) {
        const serialsArray = value.split(',').map(s => s.trim()).filter(s => s.length > 0);
        // Add each serial number individually to avoid duplicates
        serialsArray.forEach(serial => {
          dispatch(addSerialNumber(serial));
        });
      } else {
        dispatch(addSerialNumber(value));
      }
      setInputValue('');
    }
  };

  const handleDeleteSerial = (serialToDelete: string) => {
    dispatch(removeSerialNumber(serialToDelete));
  };

  const handleClearAll = () => {
    dispatch(clearAllSerialNumbers());
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAddSerial();
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        Serial Numbers (Optional)
      </Typography>
      
      {/* Input Section */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'flex-start' }}>
        <TextField
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Enter serial number(s), separated by commas"
          size="small"
          disabled={disabled}
          sx={{ flexGrow: 1 }}
          helperText="Press Enter or click Add to add serial numbers"
        />
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAddSerial}
          disabled={disabled || !inputValue.trim()}
          sx={{ height: 40 }}
        >
          Add
        </Button>
      </Box>

      {/* Chips Display */}
      {serialNumbers.length > 0 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {serialNumbers.length} serial number{serialNumbers.length !== 1 ? 's' : ''} added
            </Typography>
            <Tooltip title="Clear all serial numbers">
              <IconButton
                size="small"
                onClick={handleClearAll}
                disabled={disabled}
                color="error"
              >
                <Clear />
              </IconButton>
            </Tooltip>
          </Box>
          
          <Stack
            direction="row"
            spacing={1}
            sx={{
              flexWrap: 'wrap',
              gap: 1,
              p: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              backgroundColor: 'grey.50',
              minHeight: 56,
              alignItems: 'flex-start',
              alignContent: 'flex-start'
            }}
          >
            {serialNumbers.map((serial, index) => (
              <Chip
                key={`${serial}-${index}`}
                label={serial}
                onDelete={disabled ? undefined : () => handleDeleteSerial(serial)}
                color="primary"
                variant="outlined"
                size="small"
                sx={{ m: 0.5 }}
              />
            ))}
          </Stack>
        </Box>
      )}

      {serialNumbers.length === 0 && (
        <Box
          sx={{
            p: 2,
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 1,
            textAlign: 'center',
            backgroundColor: 'grey.50',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No serial numbers added yet. Add them above to include in the search.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default SerialNumberChips; 